import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  HostListener,
  forwardRef,
  OnInit,
  ElementRef
} from '@angular/core';
import { NgForm, UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, get, template } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { CheckUnsavedForms } from '../../interfaces/check-unsaved-forms';
import MicroModal from 'micromodal';
import { Store } from '@ngxs/store';
import { MatchValuesValidator, UserService } from 'remarketing-angular-library';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { take, takeUntil } from 'rxjs/operators';
import { SaComponent, AnalyticsService } from 'smartauction-frontend-micro-library';
import { SaveChangesModalComponent } from '../save-changes-modal/save-changes-modal.component';
import { ComplexityIndicatorComponent } from 'remarketing-ui-components-lib';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { SuccessMessageComponent } from '../success-message/success-message.component';


@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    providers: [
        {
            provide: CheckUnsavedForms,
            useExisting: forwardRef(() => ChangePasswordComponent)
        }
    ],
    imports: [SuccessMessageComponent, ErrorMessageComponent, FormsModule, ReactiveFormsModule, ComplexityIndicatorComponent, SaveChangesModalComponent]
})
export class ChangePasswordComponent extends SaComponent implements OnInit, CheckUnsavedForms {
  readonly CONSTANTS: any = {
    title: '',
    instructions: '',
    oldPassword: '',
    newPassword: '',
    reenterPassword: '',
    submit: '',
    cancel: '',

    successMessage: '',

    reviewErrors: '',
    formInvalidSingular: '',
    formInvalidPlural: '',

    usernameError: '',
    usernameGenericError: '',
    usernameNotUnique: '',
    usernameHasSpecialCharacters: '',

    passwordOldEmptyError: '',

    passwordConfirmEmpty: '',
    passwordConfirmNotMatch: '',

    passwordEmptyError: '',
    passwordLowercaseError: '',
    passwordNumberError: '',
    passwordLengthError: '',
    passwordUppercaseError: '',
    passwordGreaterThanMax: '',
    passwordContainsUsername: '',
    errorWrongPassword: '',
    errorPreviousPassword: ''
  };

  @Output() closeParentAccordion = new EventEmitter();

  @ViewChild('ngForm') ngForm: NgForm;

  credentialsForm: UntypedFormGroup;
  formSubmitAttempted = false;
  didSaveSucceed = false;
  didSaveFail = false;
  messageSaveFail: any;

  readonly modalId: string = 'change-password__save-changes-modal';

  private cancelEditing$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('pageHeading') pageHeading: ElementRef;
  routerState: any;

  passwordRules;

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private readonly router: Router,
    private readonly translocoService: TranslocoService
  ) {
    super();
    this.formSubmitAttempted = false;
    this.buildForm();
    this.buildMessageSaveFail(null);

    const currentNaviation = this.router.currentNavigation();

    if (currentNaviation && currentNaviation.extras && currentNaviation.extras.state) {
      this.routerState = currentNaviation.extras.state;
    }
  }

  ngOnInit() {
    this.getTranslations();
    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Profile Settings:My Information:Change Password`,
        section: 'Profile Settings',
        subsection: 'My Information',
        title: 'Change Password'
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.routerState && this.routerState.focus) {
      this.pageHeading.nativeElement.focus();
    }
  }

  get snapshot(): any {
    return this.store.snapshot();
  }

  get username(): string {
    return get(this.snapshot, 'user.userProfile.userId', '');
  }

  get controlPasswordOld(): AbstractControl {
    return get(this.credentialsForm, 'controls.passwordOld');
  }
  get controlPasswordNew(): AbstractControl {
    return get(this.credentialsForm, 'controls.passwordNew');
  }
  get controlPasswordConfirm(): AbstractControl {
    return get(this.credentialsForm, 'controls.passwordConfirmation');
  }

  get isPasswordOldInvalid(): boolean {
    const isInvalid = this.formSubmitAttempted && this.controlPasswordOld.invalid;
    return this.isPasswordOldWrongPassword || isInvalid;
  }
  get isPasswordOldEmpty(): boolean {
    return this.formSubmitAttempted && this.controlPasswordOld.value.length === 0;
  }
  get isPasswordOldWrongPassword(): boolean {
    return get(this.messageSaveFail, 'oldPassword', false);
  }

  get isPasswordNewInvalid(): boolean {
    const isValid = this.formSubmitAttempted && this.controlPasswordNew.invalid;
    return this.isPasswordNewPreviousPassword || isValid;
  }
  get isPasswordNewPreviousPassword(): boolean {
    return get(this.messageSaveFail, 'newPassword', false);
  }
  get isPasswordNewEmpty(): boolean {
    return this.formSubmitAttempted && this.controlPasswordNew.value.length === 0;
  }
  get passwordNewValidLength(): boolean {
    const password = this.controlPasswordNew.value;
    return !this.isPasswordNewEmpty && this.formSubmitAttempted && password.length < 8;
  }
  get isPasswordNewGreaterThanMax(): boolean {
    const isGreater = this.controlPasswordNew.value.length > 20;
    return !this.isPasswordNewEmpty && this.formSubmitAttempted && isGreater;
  }
  get isPasswordNewDifferentThanUsername(): boolean {
    const isMatchingUsername = get(this.controlPasswordNew, 'errors.passwordContainsUsername', false);
    return !this.isPasswordNewEmpty && this.formSubmitAttempted && isMatchingUsername;
  }
  get passwordNewHasLowercase(): boolean {
    const regex = /(?=.*[a-z])/;
    return !this.isPasswordNewEmpty && this.passwordRegexCheck(regex);
  }
  get passwordNewHasNumber(): boolean {
    const regex = /(?=.*\d)/;
    return !this.isPasswordNewEmpty && this.passwordRegexCheck(regex);
  }
  get passwordNewHasUppercase(): boolean {
    const regex = /(?=.*[A-Z])/;
    return !this.isPasswordNewEmpty && this.passwordRegexCheck(regex);
  }

  get isPasswordConfirmationInvalid(): boolean {
    const isValid = this.formSubmitAttempted && this.controlPasswordConfirm.invalid;
    return this.isPasswordConfirmEmpty || this.isPasswordConfirmNotMatch || isValid;
  }
  get isPasswordConfirmEmpty(): boolean {
    return this.formSubmitAttempted && this.controlPasswordConfirm.value.length === 0;
  }
  get isPasswordConfirmNotMatch(): boolean {
    const isMatchingPasswordNew = get(this.controlPasswordConfirm, 'errors.matchValues', false);
    return this.formSubmitAttempted && !this.isPasswordConfirmEmpty && isMatchingPasswordNew;
  }

  get totalNumberErrors(): number {
    const controls = this.credentialsForm.controls;
    return filter(controls, control => control.invalid).length;
  }

  get isFormDirty(): boolean {
    return !!Object.values(this.credentialsForm.value).join('');
  }

  get isFormInvalid(): boolean {
    //if check added to avoid undefined type error when running locally
    if (this.ngForm) {
      return (this.ngForm.submitted && this.totalNumberErrors > 0) || false;
    } else {
      return false;
    }
  }

  get displayFormErrorsMessage(): string {
    const isPlural = this.totalNumberErrors > 1,
      tmplName = isPlural ? 'formInvalidPlural' : 'formInvalidSingular';
    return template(this.CONSTANTS[tmplName])({ n: this.totalNumberErrors });
  }

  restrictPwd(event: any): void {
    const noSpecialPattern = /[<>&%=?]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (noSpecialPattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  onSubmit(): void {
    this.formSubmitAttempted = true;
    this.didSaveFail = false;
    this.didSaveSucceed = false;

    if (this.credentialsForm.valid) {
      this.requestUpdatePassword().pipe(take(1)).subscribe(
        () => this.createUserResponseSuccess(),
        err => this.createUserResponseFailure(err)
      );
    } else {
      console.log('errors');
      this.focusElement('reviewErrors');
    }
  }

  cancelEditing(): void {
    this.cancelEditing$.next(true);
    this.cancelEditing$.complete();
    this.closeParentAccordion.emit();
  }

  continueEditing(): void {
    this.cancelEditing$.next(false);
    this.cancelEditing$.complete();
  }

  hasUnsavedForms(): boolean {
    return this.isFormDirty;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    event.preventDefault();
    if (this.hasUnsavedForms()) {
      event.returnValue = true;
    }
  }

  showNavigationAlert(): Observable<boolean> {
    this.openMicroModal(this.modalId);
    this.cancelEditing$ = new Subject<boolean>();
    return this.cancelEditing$.asObservable();
  }

  showAlert(): void {
    this.openMicroModal(this.modalId);
  }

  openMicroModal(modalId: string): void {
    MicroModal.show(modalId, {
      disableScroll: true
    });
    const heading = document.querySelector(`#${modalId} h1`) as HTMLElement;
    if (heading) {
      heading.focus();
    }
  }

  private resetForm(): void {
    this.buildForm();
    this.formSubmitAttempted = false;
    (this.ngForm as any).submitted = false;
  }

  private buildForm(): void {
    this.credentialsForm = this.formBuilder.group({
      passwordOld: ['', Validators.compose([Validators.required])],
      passwordNew: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          this.validatePasswordComplexity,
          this.validatePasswordNotMatchUsername
        ])
      ],
      passwordConfirmation: ['', Validators.compose([Validators.required, MatchValuesValidator('passwordNew')])]
    });

    this.credentialsForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(values => {
      if (this.didSaveFail || this.didSaveSucceed) {
        this.buildMessageSaveFail(null);
        this.didSaveFail = false;
        this.didSaveSucceed = false;
      }
    });
  }

  private requestUpdatePassword(): Observable<any> {
    const payload = {
      oldPassword: this.controlPasswordOld.value,
      newPassword: this.controlPasswordNew.value
    };

    if (sessionStorage.getItem('allowUpdatePassword')) {
      return of(payload);
    }

    return this.userService.updatePassword(payload);
  }

  private createUserResponseSuccess(): void {
    this.didSaveSucceed = true;
    this.resetForm();
  }

  private createUserResponseFailure(err: any): void {
    this.didSaveFail = true;
    this.buildMessageSaveFail(err.status);
  }

  private buildMessageSaveFail(status?: number): void {
    switch (status) {
    case 401:
      this.messageSaveFail = { oldPassword: true };
      break;

    case 409:
      this.messageSaveFail = { newPassword: true };
      break;

    default:
      this.messageSaveFail = {};
    }
    const isPlural = this.totalNumberErrors > 1;
    if (isPlural) {
      this.focusElement('reviewErrors');
    }
  }

  private passwordRegexCheck(regex: RegExp): boolean {
    const password = this.controlPasswordNew.value;
    return this.formSubmitAttempted && !regex.test(password);
  }

  private validatePasswordComplexity = (c: UntypedFormControl): any => {
    const regexp = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/;
    const isValid = regexp.test(c.value);

    if (!isValid) {
      return { validatePassword: { valid: false } };
    }

    return null;
  };

  private validatePasswordNotMatchUsername = (control: UntypedFormControl): any => {
    if (control.value.toLowerCase().includes(this.username.toLowerCase())) {
      return { passwordContainsUsername: true };
    }

    return null;
  };

  focusElement(id) {
    setTimeout(() => {
      const message = document.getElementById(id) as HTMLElement;
      if (message) {
        message.focus();
      }
    }, 100);
  }

  getTranslations(){
    const lowerCaseUsername = this.username.toLowerCase();
    this.translocoService.selectTranslateObject('profileManagement.changePassword').pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.CONSTANTS.title = translate.textChangePass;
      this.CONSTANTS.instructions = translate.textNewPassContain;
      this.CONSTANTS.oldPassword = translate.textCurrentPass;
      this.CONSTANTS.newPassword = translate.textNewPass;
      this.CONSTANTS.reenterPassword = translate.textReEnterPass;
      this.CONSTANTS.submit = translate.textChangePass;
      this.CONSTANTS.cancel = translate.textCancel;
      this.CONSTANTS.successMessage = translate.textSuccessfullyChange;
      this.CONSTANTS.reviewErrors = translate.textReviewErrors;
      this.CONSTANTS.formInvalidSingular = translate.textErrorSubmit;
      this.CONSTANTS.formInvalidPlural = translate.textNError;
      this.CONSTANTS.usernameError = translate.textEnterUserName;
      this.CONSTANTS.usernameGenericError = translate.textUserNameContain;
      this.CONSTANTS.usernameNotUnique = translate.textUserNameUse;
      this.CONSTANTS.usernameHasSpecialCharacters = translate.textUserNameLeastContain;
      this.CONSTANTS.passwordOldEmptyError = translate.textEnterCurrentPass;
      this.CONSTANTS.passwordConfirmEmpty = translate.textReEnterNewPass;
      this.CONSTANTS.passwordConfirmNotMatch = translate.textPassMatch;
      this.CONSTANTS.passwordEmptyError = translate.textEnterNewPass;
      this.CONSTANTS.passwordLowercaseError = translate.textPassLowerCase;
      this.CONSTANTS.passwordNumberError = translate.textPassNumber;
      this.CONSTANTS.passwordLengthError = translate.textPassCharacter;
      this.CONSTANTS.passwordUppercaseError = translate.textPassUpperCase;
      this.CONSTANTS.passwordGreaterThanMax = translate.textPassLimitCharacter;
      this.CONSTANTS.passwordContainsUsername = translate.textPassCantUsername;
      this.CONSTANTS.errorWrongPassword = translate.textCurrentPassDoesnt;
      this.CONSTANTS.errorPreviousPassword = translate.textPassReUse;
      this.passwordRules = [
        {
          label: translate.ruleCharacter,
          isValid(value: string): boolean {
            return value.length >= 8 && value.length <= 20;
          }
        },
        {
          label: translate.ruleUppercaseLetter,
          isValid(value: string): boolean {
            return /(?=.*[A-Z])/.test(value);
          }
        },
        {
          label: translate.ruleLowercaseLetter,
          isValid(value: string): boolean {
            return /(?=.*[a-z])/.test(value);
          }
        },
        {
          label: translate.ruleNumber,
          isValid(value: string): boolean {
            return /(?=.*\d)/.test(value);
          }
        },
        {
          label: translate.ruleCantUserName,
          isValid(value: string): boolean {
            return value && !(value.toLocaleLowerCase().includes(lowerCaseUsername));
          }
        }
      ];
    });
  }
}
